import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  User,
  Building2,
  Mail,
  Phone,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { contactText } from "i18n/contactCTA";
import toast from "react-hot-toast";
import ProcessingBar from "Util/Processingbar";
import { container, fadeUp, slideLeft } from "animations/variants";

const FullContactForm = () => {
  const lang = useSelector((state) => state.language.lang);
  const location = useLocation();
  const data = location.state || {};

  const text = contactText[lang || "en"];
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    fullName: data.fullName || "",
    businessName: data.businessName || "",
    email: data.email || "",
    phone: data.phone || "",
    service: data.service || "",
    preferredTime: data.preferredTime || "",
    message: data.message || "",
  });

  const updateField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const phoneRegex = /^[+\d\s()-]{7,20}$/;

    const e = {};
    if (!form.fullName.trim()) e.fullName = text.errors.fullName;
    if (!form.email.trim()) e.email = text.errors.email;
    else if (!/\S+@\S+\.\S+/.test(form.email))
      e.email = text.errors.invalidEmail;
    if (!form.service) e.service = text.errors.service;
    if (!form.message.trim()) e.message = text.errors.message;
    if (form.phone.trim() && !phoneRegex.test(form.phone)) {
      e.phone = text.errors.invalidPhone;
    }
    if (!form.businessName.trim()) {
      e.businessName = text.errors.business;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // const store = {
  //   YOUR_SERVICE_ID: "service_ebww48d",
  //   YOUR_TEMPLATE_ID: "template_pdv5cg9",
  //   YOUR_PUBLIC_KEY: "5t5zOM8-bVTxA2N7V",
  // };

  const store = {
    YOUR_SERVICE_ID: "maria_advisory",
    YOUR_TEMPLATE_ID: "template_zelihz4",
    YOUR_PUBLIC_KEY: "F9OrLv6WXUaY2ldGa",
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    console.log("Form data:", form); // Log the form data for debugging

    try {
      setLoading(true);

      const response = await emailjs.send(
        store.YOUR_SERVICE_ID,
        store.YOUR_TEMPLATE_ID,
        {
          fullName: form.fullName,
          businessName: form.businessName,
          email: form.email,
          phone: form.phone,
          service: form.service,
          preferredTime: form.preferredTime,
          user_message: form.message,
        },
        store.YOUR_PUBLIC_KEY,
      );

      console.log(response);
      // { status: 200, text: "OK" }

      if (response.status === 200) {
        toast.success(
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src="/maria/contact.png" // Change this to your logo path
              alt="Logo"
              style={{
                width: "auto",
                height: "30px",
                marginRight: "10px",
                borderRadius: "50%",
              }}
            />
            <span>
              Your enquiry has been submitted successfully. Thank you for
              contacting Maria Advisory. One of our team members will review
              your request and get back to you as soon as possible.
            </span>
          </div>,
        );

        await new Promise((resolve) => setTimeout(resolve, 2000));

        // setSent(true);

        setForm({
          fullName: "",
          businessName: "",
          email: "",
          phone: "",
          service: "",
          preferredTime: "",
          message: "",
        });

        setErrors({});
      } else {
        toast.error("Unable to send your enquiry. Please try again.");
      }
    } catch (error) {
      console.error(error);

      toast.error("Failed to send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  if (loading) {
    return (
      <section className="relative min-h-screen overflow-hidden bg-[#f7f3ef] flex items-center justify-center px-4 py-8">
        {/* Background */}
        <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full bg-reddark/10 blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-neonYellow/10 blur-3xl" />

        <div
          className="
      relative
      w-full
      max-w-lg
      rounded-3xl
      bg-darkbg
      border border-black/10
      shadow-2xl
      overflow-hidden
    "
        >
          <div className="h-1 bg-gradient-to-r from-reddark via-neonYellow to-reddark" />

          <div className="px-6 py-8 md:px-10 md:py-12 text-center">
            {/* Logo */}
            <div className="relative mx-auto w-20 h-20 md:w-28 md:h-28">
              <div className="absolute inset-0 rounded-full bg-reddark/20 blur-xl animate-pulse" />

              <div className="relative w-full h-full rounded-full bg-white border border-black/10 overflow-hidden">
                <img
                  src="/maria/contact.png"
                  alt="Maria Advisory"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 mt-5 px-3 py-1 rounded-full bg-neonYellow/10 text-neonYellow text-[10px] md:text-xs uppercase tracking-widest font-semibold">
              <span className="w-2 h-2 rounded-full bg-neonYellow animate-pulse" />
              Secure Submission
            </div>

            {/* Title */}
            <h2 className="mt-5 font-[Crimson] text-3xl md:text-4xl leading-tight text-reddark">
              Sending Your Enquiry
            </h2>

            {/* Description */}
            <p className="mt-4 text-sm md:text-base text-gray-600 leading-7 max-w-sm mx-auto">
              We're securely sending your enquiry to our team. Please keep this
              page open while we complete the submission.
            </p>

            {/* Progress */}
            <div className="mt-8">
              <ProcessingBar />
            </div>

            {/* Footer */}
            <div className="mt-8 pt-5 border-t border-black/10">
              <p className="text-xs md:text-sm text-gray-500 italic">
                Average processing time:
                <span className="text-reddark font-semibold"> 3–5 seconds</span>
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <motion.section
      className="relative overflow-x-hidden bg-[#f7f3ef] min-h-screen py-24 px-4 sm:px-6"
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
    >
      {" "}
      <div className="max-w-7xl mx-auto rounded-xl my-8">
        {/* ------------------ */}
        <motion.div
          className="absolute -top-20 -right-20 w-72 h-72 bg-reddark/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.08, 1],
            opacity: [0.2, 0.35, 0.2],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />{" "}
        <div className="p-0 md:p-0">
          <motion.div className="max-w-3xl" variants={slideLeft}>
            {" "}
            {/* <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-[1px] bg-reddark" />

              <span className="text-xs uppercase tracking-[0.3em] text-reddark font-semibold">
                Maria Advisory
              </span>
            </div> */}
            <h1 className="font-[Crimson] text-3xl md:text-5xl text-[#111] leading-tight mt-16 mb-4">
              {text.title}
            </h1>
            {/* <p className="mt-4 text-[#666] leading-relaxed max-w-2xl">
              {text.subtitle}
            </p> */}
          </motion.div>

          {/* ----------- */}
          <div className="h-2 bg-red-800" />
          <motion.div className="py-4" variants={fadeUp}>
            {" "}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div variants={fadeUp}>
                <Field
                  label={text.name}
                  value={form.fullName}
                  error={errors.fullName}
                  onChange={(v) => updateField("fullName", v)}
                  icon={User}
                />
              </motion.div>
              <motion.div variants={fadeUp}>
                <Field
                  label={text.business}
                  value={form.businessName}
                  error={errors.businessName}
                  onChange={(v) => updateField("businessName", v)}
                  icon={Building2}
                />
              </motion.div>
              <motion.div variants={fadeUp}>
                <Field
                  label={text.email}
                  value={form.email}
                  error={errors.email}
                  onChange={(v) => updateField("email", v)}
                  icon={Mail}
                />
              </motion.div>
              <motion.div variants={fadeUp}>
                <Field
                  label={text.phone}
                  value={form.phone}
                  error={errors.phone}
                  onChange={(v) => updateField("phone", v)}
                  icon={Phone}
                />
              </motion.div>
            </div>
            <motion.div className="mt-8" variants={fadeUp}>
              {" "}
              <label className="font-semibold text-textGray">
                {text.service}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                {text.services.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateField("service", s)}
                    className={`
  group
  p-4
  rounded-2xl
  border
  text-sm
  font-medium
  transition-all
  duration-300
  text-left
hover:bg-reddark
hover:text-white
  ${
    form.service === s
      ? `
        bg-reddark
        text-white
        border-reddark
        shadow-lg
      `
      : `
        bg-[#faf7f4]
        text-[#333]
        border-[#e5d8cc]
        hover:border-reddark
        hover:-translate-y-1
      `
  }
`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {errors.service && (
                <p className="text-red-600 text-sm mt-2">{errors.service}</p>
              )}
            </motion.div>
            {/* <div className="mt-8">
              <label className="font-semibold text-textGray">{text.time}</label>
              <div className="flex gap-3 mt-3 flex-wrap">
                {text.times.map((t) => (
                  <button
                    key={t}
                    onClick={() => updateField("preferredTime", t)}
                    className={`px-4 py-2 rounded-xl border ${form.preferredTime === t ? "bg-red-800 text-white" : "bg-[#f7f3ef] text-[#333] border-[#ddd]"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div> */}
            <div className="mt-8">
              <label className="font-semibold text-textGray">
                {text.message}
              </label>
              <textarea
                rows={7}
                value={form.message}
                onChange={(e) => updateField("message", e.target.value)}
                className={`w-full mt-2 p-4 rounded-2xl text-text border ${errors.message ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.message && (
                <p className="text-red-600 text-sm mt-2">{errors.message}</p>
              )}
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              className="mt-8 bg-red-800 text-white px-8 py-4 rounded-2xl flex items-center gap-3"
            >
              <MessageSquare size={18} />
              {loading ? text.sending : text.submit}
              <ArrowRight size={18} />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

const Field = ({ label, value, onChange, error, icon: Icon }) => (
  <div className="min-w-0">
    {" "}
    <label className="block mb-2 font-semibold text-textGray">{label}</label>
    <div className="relative">
      <Icon
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        size={18}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`
  w-full
  min-w-0
  pl-12
  pr-4
  py-4
  rounded-2xl
  border
  bg-[#faf7f4]
  text-[#222]
  placeholder:text-[#999]
  transition-all
  focus:outline-none
  focus:border-reddark
  focus:bg-white

  ${error ? "border-red-500" : "border-[#e5d8cc]"}
`}
      />
    </div>
    {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
  </div>
);

export default FullContactForm;
